import type { CompanyRegistrationValues } from "../model/registrationTypes";
import {
  ChangeAdvokatselskabKind,
  ChangeInteressentskabKind,
  CompanyType,
  JaNej,
  WhatToRegister,
} from "../model/registrationEnums";
import {
  isEntersAdvokatselskabPartner,
  isEntersInterestParty,
} from "../change/changePartyEnterRules";
import {
  syncChangeAdvokatselskabEnteringPartyEligibility,
  syncChangeInteressentskabEnteringPartyEligibility,
  syncInteressentskabParticipantEligibility,
} from "../eligibility/participantEligibilityRules";
import {
  clearChangePartyDirectionsWhenNotBegge,
  changePartyRowChanged,
  syncChangePartyEligibilityRows,
} from "./changePartySync";
import {
  creationParticipantEligibilityChanged,
  syncCreationParticipantEligibilityRows,
} from "./participantEligibilitySync";
import {
  clearInvoiceDetailsNotMatchingTarget,
  clearTextFieldsIfSet,
  resetEmployeesIfNeeded,
  syncArrayFieldIfChanged,
  type SetFieldValue,
} from "./syncRegistrationFieldHelpers";

const syncInvalidCompanyTypeForChange = (
  values: CompanyRegistrationValues,
  setFieldValue: SetFieldValue,
): void => {
  if (
    values.whatToRegister === WhatToRegister.Change &&
    values.companyType === CompanyType.Enkeltmandsvirksomhed
  ) {
    void setFieldValue("companyType", "", false);
  }
};

const syncEnkeltmandsvirksomhedFields = (
  values: CompanyRegistrationValues,
  setFieldValue: SetFieldValue,
): void => {
  const block = values.enkeltmandsvirksomhed;

  clearInvoiceDetailsNotMatchingTarget(
    setFieldValue,
    "enkeltmandsvirksomhed",
    block.invoiceTarget,
    block.invoiceEmail,
    block.eanNumber,
  );

  if (block.startFromOtherCompany !== JaNej.Ja) {
    clearTextFieldsIfSet(setFieldValue, [
      ["enkeltmandsvirksomhed.otherCompanyName", block.otherCompanyName],
      ["enkeltmandsvirksomhed.otherCompanyEndDate", block.otherCompanyEndDate],
    ]);
  }

  if (block.employees !== JaNej.Ja) {
    resetEmployeesIfNeeded(
      setFieldValue,
      "enkeltmandsvirksomhed",
      block.employeeList,
    );
  }
};

const syncInteressentskabFields = (
  values: CompanyRegistrationValues,
  setFieldValue: SetFieldValue,
): void => {
  const block = values.interessentskab;

  clearInvoiceDetailsNotMatchingTarget(
    setFieldValue,
    "interessentskab",
    block.invoiceTarget,
    block.invoiceEmail,
    block.eanNumber,
  );

  if (block.employees !== JaNej.Ja) {
    resetEmployeesIfNeeded(
      setFieldValue,
      "interessentskab",
      block.employeeList,
    );
  }

  const syncedParticipants = block.responsibleParticipants.map(
    syncInteressentskabParticipantEligibility,
  );

  syncArrayFieldIfChanged(
    setFieldValue,
    "interessentskab.responsibleParticipants",
    block.responsibleParticipants,
    syncedParticipants,
    changePartyRowChanged,
  );
};

const syncAdvokatselskabFields = (
  values: CompanyRegistrationValues,
  setFieldValue: SetFieldValue,
): void => {
  const block = values.advokatselskab;

  clearInvoiceDetailsNotMatchingTarget(
    setFieldValue,
    "advokatselskab",
    block.invoiceTarget,
    block.invoiceEmail,
    block.eanNumber,
  );

  if (block.employees !== JaNej.Ja) {
    resetEmployeesIfNeeded(setFieldValue, "advokatselskab", block.employeeList);
  }

  const syncedShareholders = syncCreationParticipantEligibilityRows(
    block.shareholders,
  );

  syncArrayFieldIfChanged(
    setFieldValue,
    "advokatselskab.shareholders",
    block.shareholders,
    syncedShareholders,
    creationParticipantEligibilityChanged,
  );
};

const syncChangeInteressentskabFields = (
  values: CompanyRegistrationValues,
  setFieldValue: SetFieldValue,
): void => {
  const block = values.changeInteressentskab;

  const clearedDirections = clearChangePartyDirectionsWhenNotBegge(
    block.interestParties,
    block.changeKind,
    ChangeInteressentskabKind.InteressentBegge,
  );

  if (clearedDirections) {
    void setFieldValue(
      "changeInteressentskab.interestParties",
      clearedDirections,
      false,
    );
  }

  const syncedInterestParties = syncChangePartyEligibilityRows(
    block.interestParties,
    block.changeKind,
    isEntersInterestParty,
    syncChangeInteressentskabEnteringPartyEligibility,
  );

  syncArrayFieldIfChanged(
    setFieldValue,
    "changeInteressentskab.interestParties",
    block.interestParties,
    syncedInterestParties,
    changePartyRowChanged,
  );
};

const syncChangeAdvokatselskabFields = (
  values: CompanyRegistrationValues,
  setFieldValue: SetFieldValue,
): void => {
  const block = values.changeAdvokatselskab;

  const clearedDirections = clearChangePartyDirectionsWhenNotBegge(
    block.partners,
    block.changeKind,
    ChangeAdvokatselskabKind.PartnerBegge,
  );

  if (clearedDirections) {
    void setFieldValue(
      "changeAdvokatselskab.partners",
      clearedDirections,
      false,
    );
  }

  const syncedPartners = syncChangePartyEligibilityRows(
    block.partners,
    block.changeKind,
    isEntersAdvokatselskabPartner,
    syncChangeAdvokatselskabEnteringPartyEligibility,
  );

  syncArrayFieldIfChanged(
    setFieldValue,
    "changeAdvokatselskab.partners",
    block.partners,
    syncedPartners,
    changePartyRowChanged,
  );
};

export const syncConditionalRegistrationFields = (
  values: CompanyRegistrationValues,
  setFieldValue: SetFieldValue,
): void => {
  syncInvalidCompanyTypeForChange(values, setFieldValue);
  syncEnkeltmandsvirksomhedFields(values, setFieldValue);
  syncInteressentskabFields(values, setFieldValue);
  syncAdvokatselskabFields(values, setFieldValue);
  syncChangeInteressentskabFields(values, setFieldValue);
  syncChangeAdvokatselskabFields(values, setFieldValue);
};
